import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { marginApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { usePlanFeature } from '../components/plan-gate.tsx';
import { Alert, Button, EmptyState, LoadingBlock, PageHeader, TextInput } from '../components/ui.tsx';
import { formatMarginPercent } from '../components/quote/quote-margin-panel.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { getDateLocale } from '../i18n/locale.ts';
import { formatShortInstant } from '../i18n/datetime.ts';
import type { QuoteMarginOverride } from '@shared/types/index.ts';

/**
 * Below-floor discounts (migration 0118): requests waiting on an owner or
 * manager, the recent trail (who applied each discount, who decided it, why,
 * and what it did to the margin), and — for the owner and managers — how
 * often each salesperson discounts below the floor.
 *
 * An estimator sees their own requests. Staff only; Pro (`profit_insights`).
 */
export default function MarginOverridesPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('marginOverrides.title'));
  const queryClient = useQueryClient();
  const { can, company } = useSession();
  const allowed = usePlanFeature('profit_insights') && can('quotes:read');
  const canDecide = can('quotes:price_override');
  const canReport = can('reports:read');
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const pending = useQuery({
    queryKey: ['margin-overrides', 'pending'],
    queryFn: () => marginApi.overrides({ status: 'pending' }),
    enabled: allowed,
  });
  const recent = useQuery({
    queryKey: ['margin-overrides', 'recent'],
    queryFn: () => marginApi.overrides({ limit: 50 }),
    enabled: allowed,
  });
  const report = useQuery({
    queryKey: ['margin-overrides', 'report'],
    queryFn: () => marginApi.report(),
    enabled: allowed && canReport,
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['margin-overrides'] });
  const decide = useMutation({
    mutationFn: async ({ id, approve }: { id: string; approve: boolean }) =>
      approve ? marginApi.approve(id, notes[id]) : marginApi.reject(id, notes[id]),
    onSuccess: (_result, variables) => {
      setMessage({
        tone: 'success',
        text: variables.approve ? t('marginOverrides.approved') : t('marginOverrides.rejected'),
      });
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['quote-margin'] });
    },
    onError: (caught) =>
      setMessage({
        tone: 'error',
        text: caught instanceof ApiRequestError ? caught.message : t('marginOverrides.decideError'),
      }),
  });

  if (!allowed) {
    return (
      <div>
        <PageHeader title={t('marginOverrides.title')} />
        <Alert tone="info">{t('marginOverrides.unavailable')}</Alert>
      </div>
    );
  }

  const money = (row: QuoteMarginOverride, cents: number) => formatCents(cents, row.currencyCode, getDateLocale());
  const discountLabel = (row: QuoteMarginOverride) =>
    row.discountType === 'percentage' ? `${(row.discountValue / 100).toFixed(1)}%` : money(row, row.discountValue);

  // A render function, not a component defined here: a new component type on
  // every render would remount each row and drop focus from the note input.
  const renderRow = (row: QuoteMarginOverride, actions: boolean) => (
    <li key={row.id} className="py-3" data-testid="margin-override-row">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-semibold">
          <Link to={`/quotes/${row.quoteId}`} className="underline">
            {row.quoteNumber ?? t('marginOverrides.quote')}
          </Link>
          {row.customerName ? ` · ${row.customerName}` : ''}
        </p>
        <span className="text-sm text-[color:var(--color-muted)]">
          {t(`marginOverrides.status.${row.status}`)} · {formatShortInstant(row.createdAt)}
        </span>
      </div>
      <p className="text-sm">
        {t('marginOverrides.summary', {
          person: row.requestedBy.name ?? t('marginOverrides.someone'),
          discount: discountLabel(row),
          amount: money(row, row.discountCents),
          margin: formatMarginPercent(row.lowestMarginBasisPoints),
          floor: formatMarginPercent(row.floorBasisPoints),
        })}
      </p>
      <p className="text-sm">
        <span className="text-[color:var(--color-muted)]">{t('marginOverrides.reason')}:</span> {row.reason}
      </p>
      {row.decidedBy ? (
        <p className="text-sm text-[color:var(--color-muted)]">
          {t('marginOverrides.decidedBy', {
            person: row.decidedBy.name ?? t('marginOverrides.someone'),
            when: row.decidedAt ? formatShortInstant(row.decidedAt) : '',
          })}
          {row.decisionNote ? ` — ${row.decisionNote}` : ''}
        </p>
      ) : null}
      {actions ? (
        <div className="mt-2">
          <TextInput
            label={t('marginOverrides.note')}
            value={notes[row.id] ?? ''}
            onChange={(event) => setNotes((current) => ({ ...current, [row.id]: event.target.value }))}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              busy={decide.isPending && decide.variables?.id === row.id && decide.variables.approve}
              onClick={() => decide.mutate({ id: row.id, approve: true })}
            >
              {t('marginOverrides.approve')}
            </Button>
            <Button
              busy={decide.isPending && decide.variables?.id === row.id && !decide.variables.approve}
              onClick={() => decide.mutate({ id: row.id, approve: false })}
            >
              {t('marginOverrides.reject')}
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );

  return (
    <div>
      <PageHeader title={t('marginOverrides.title')} description={t('marginOverrides.description')} />
      {message ? (
        <div className="mb-4">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      ) : null}

      <section className="card mb-6" aria-labelledby="margin-pending-heading">
        <h2 id="margin-pending-heading" className="mb-2 text-lg font-semibold">
          {canDecide ? t('marginOverrides.waitingOnYou') : t('marginOverrides.yourPending')}
        </h2>
        {pending.isLoading ? <LoadingBlock /> : null}
        {pending.data && pending.data.length === 0 ? <EmptyState title={t('marginOverrides.noPending')} /> : null}
        <ul className="divide-y divide-[color:var(--color-line)]">
          {(pending.data ?? []).map((row) => renderRow(row, canDecide))}
        </ul>
      </section>

      {canReport ? (
        <section className="card mb-6" aria-labelledby="margin-report-heading">
          <h2 id="margin-report-heading" className="mb-1 text-lg font-semibold">
            {t('marginOverrides.reportHeading')}
          </h2>
          {report.data ? (
            <p className="mb-3 text-sm text-[color:var(--color-muted)]">
              {t('marginOverrides.reportRange', { from: report.data.from, to: report.data.to })}
            </p>
          ) : null}
          {report.isLoading ? <LoadingBlock /> : null}
          {report.data && report.data.rows.length === 0 ? (
            <EmptyState title={t('marginOverrides.reportEmpty')} />
          ) : null}
          {report.data && report.data.rows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-max text-sm" data-testid="margin-override-report">
                <thead>
                  <tr className="text-left text-[color:var(--color-muted)]">
                    <th className="py-1 pr-3 font-medium">{t('marginOverrides.salesperson')}</th>
                    <th className="py-1 pr-3 text-right font-medium">{t('marginOverrides.total')}</th>
                    <th className="py-1 pr-3 text-right font-medium">{t('marginOverrides.approvedCount')}</th>
                    <th className="py-1 pr-3 text-right font-medium">{t('marginOverrides.rejectedCount')}</th>
                    <th className="py-1 pr-3 text-right font-medium">{t('marginOverrides.pendingCount')}</th>
                    <th className="py-1 pr-3 text-right font-medium">{t('marginOverrides.discountGiven')}</th>
                    <th className="py-1 text-right font-medium">{t('marginOverrides.averageMargin')}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.rows.map((row) => (
                    <tr key={`${row.userId}/${row.currencyCode}`}>
                      <td className="py-1 pr-3">{row.name ?? t('marginOverrides.someone')}</td>
                      <td className="py-1 pr-3 text-right">{row.total}</td>
                      <td className="py-1 pr-3 text-right">{row.approved}</td>
                      <td className="py-1 pr-3 text-right">{row.rejected}</td>
                      <td className="py-1 pr-3 text-right">{row.pending}</td>
                      <td className="py-1 pr-3 text-right">
                        {formatCents(row.discountCents, row.currencyCode || company?.currencyCode, getDateLocale())}
                      </td>
                      <td className="py-1 text-right">{formatMarginPercent(row.averageLowestMarginBasisPoints)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="card mb-6" aria-labelledby="margin-recent-heading">
        <h2 id="margin-recent-heading" className="mb-2 text-lg font-semibold">
          {t('marginOverrides.recentHeading')}
        </h2>
        {recent.isLoading ? <LoadingBlock /> : null}
        {recent.data && recent.data.length === 0 ? <EmptyState title={t('marginOverrides.noRecent')} /> : null}
        <ul className="divide-y divide-[color:var(--color-line)]">
          {(recent.data ?? []).filter((row) => row.status !== 'pending').map((row) => renderRow(row, false))}
        </ul>
      </section>
    </div>
  );
}
